import { Link } from "react-router-dom";

export default function PrivacyPolicyPage() {
  document.title = "Privacy Policy | ROAS-AI";
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-300 py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/login" className="text-violet-400 hover:text-violet-300 text-sm mb-8 inline-block">&larr; Back to ROAS-AI</Link>

        <h1 className="font-heading text-4xl text-white font-bold mb-2">Privacy Policy</h1>
        <p className="text-zinc-400 mb-10">Last updated: March 2, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-white text-lg font-semibold mb-3">1. Introduction</h2>
            <p>ROAS-AI ("we", "our", "us") operates the ROAS-AI marketing campaign optimization platform at roas-ai.com (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="text-white">Account Information:</span> Name, email address, and profile picture when you sign up or log in via Google OAuth.</li>
              <li><span className="text-white">Ad Platform Data:</span> When you connect your ad accounts (Meta, Google, LinkedIn, Microsoft), we access campaign performance data including spend, impressions, clicks, conversions, and revenue through authorized OAuth connections.</li>
              <li><span className="text-white">Usage Data:</span> Pages visited, features used, and interactions within the Service.</li>
              <li><span className="text-white">Payment Information:</span> Payment details are processed by Stripe. We do not store credit card numbers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, maintain, and improve the Service</li>
              <li>To analyze and optimize your advertising campaigns across connected platforms</li>
              <li>To provide AI-powered budget recommendations and ROAS optimization</li>
              <li>To send account-related notifications (approval, password reset)</li>
              <li>To process payments for budget packages and AI tokens</li>
              <li>To ensure the security and integrity of the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">4. Ad Platform Data Access</h2>
            <p className="mb-3">When you connect your ad accounts, we request only the permissions necessary to read and manage your advertising data:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="text-white">Google Ads:</span> We access your Google Ads data to read campaign performance and manage budgets. We use the Google Ads API in compliance with Google's API Services User Data Policy.</li>
              <li><span className="text-white">Meta (Facebook) Ads:</span> We access ad account data, campaign metrics, and business management information.</li>
              <li><span className="text-white">LinkedIn Ads:</span> We access ad account data, campaign reporting, and organization information.</li>
              <li><span className="text-white">Microsoft (Bing) Ads:</span> We access campaign management data and performance metrics.</li>
            </ul>
            <p className="mt-3">You can disconnect any ad platform at any time from the Campaigns page. Upon disconnection, we stop accessing your data from that platform.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">5. Data Storage and Security</h2>
            <p>Your data is stored securely using encrypted databases. OAuth access tokens and refresh tokens are stored securely and used only to access your authorized ad platform data. We implement industry-standard security measures including HTTPS encryption, secure session management, and regular security reviews.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">6. Data Sharing</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share data only in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>With service providers who assist in operating the Service (e.g., Stripe for payments, cloud hosting providers)</li>
              <li>When required by law or to respond to legal processes</li>
              <li>To protect the rights, property, or safety of ROAS-AI, our users, or the public</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">7. Data Retention</h2>
            <p>We retain your account data and connected ad platform data for as long as your account is active. You may request deletion of your account and associated data by contacting us. Upon account deletion, we will remove your personal data and revoke all ad platform access tokens.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">8. Your Rights</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access, correct, or delete your personal data</li>
              <li>Disconnect ad platforms and revoke data access at any time</li>
              <li>Request a copy of your data</li>
              <li>Opt out of non-essential communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">9. Cookies</h2>
            <p>We use essential cookies for session management and authentication. We do not use third-party tracking cookies.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the updated policy on this page with a revised "Last updated" date.</p>
          </section>

          <section>
            <h2 className="text-white text-lg font-semibold mb-3">11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at <a href="mailto:privacy@roas-ai.com" className="text-violet-400 hover:text-violet-300">privacy@roas-ai.com</a> or write to us at: 701 Tillery Street, Unit 12-1574, Austin, Texas 78702, United States.</p>
          </section>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 text-zinc-500 text-xs text-right">
          <a href="/about" className="text-zinc-400 hover:text-zinc-400 mr-4">About Us</a>
          <a href="/terms" className="text-zinc-400 hover:text-zinc-400 mr-4">Terms</a>
          Copyright (c) 2026 Beruf LLC
        </div>
      </div>
    </div>
  );
}
